import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { fleetDetailFromRestV1, fleetsFromRestV1 } from 'domain/mappers/fleetFromRestV1';
import type { FleetDetailFE, FleetFE } from 'domain/systemAdmin';
import { client } from 'graphql/client';
import { createFleet, updateFleetById } from 'graphql/mutations';

import { errorHandler, formatAxiosError, isAxiosError } from '../errors';
import { correlationHeaders, managementRestApi } from '../restClient';

import type { CreateFleetInput, SystemFleet, UpdatedFleetInput } from 'API';
import { SystemFleetSchema } from 'APIzod';

/**
 * Mixed slice: the two reads are REST against the management service, the two mutations are still
 * GraphQL, so `graphqlRequestBaseQuery` stays — the precedent is `services/vehiclesV2.ts`.
 */
const listSystemFleetsQueryFn = async (): Promise<{ data: FleetFE[] }> => {
    try {
        const response = await managementRestApi.get<unknown>('/api/v1/fleets', correlationHeaders());
        return { data: fleetsFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('[listFleets]: Unexpected error occurred while fetching system fleets', loggableError);
    }
};

const getSystemFleetAttributeQueryFn = async (id: string): Promise<{ data: FleetDetailFE }> => {
    try {
        const response = await managementRestApi.get<unknown>(`/api/v1/fleets/${id}`, correlationHeaders());
        return { data: fleetDetailFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler(
            `[getFleetById]: Unexpected error occurred while fetching system fleet ${id} attributes`,
            loggableError
        );
    }
};

export const systemFleetsApi = createApi({
    reducerPath: 'systemFleets',
    baseQuery: graphqlRequestBaseQuery({
        url: '/graphql',
    }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        listSystemFleets: builder.query<FleetFE[], string>({
            queryFn: listSystemFleetsQueryFn,
        }),
        getSystemFleetsAttributes: builder.query<FleetDetailFE, string>({
            queryFn: getSystemFleetAttributeQueryFn,
        }),
        updateSystemFleet: builder.mutation<SystemFleet, UpdatedFleetInput>({
            queryFn: async variables => {
                try {
                    const { data } = await client.graphql({
                        query: updateFleetById,
                        variables: {
                            input: variables,
                        },
                    });
                    return { data: SystemFleetSchema().parse(data.updateFleetById) };
                } catch (error) {
                    throw errorHandler(
                        `Unexpected error occurred while updating the fleet with id: ${variables.id}`,
                        error
                    );
                }
            },
        }),
        createSystemFleet: builder.mutation<SystemFleet, CreateFleetInput>({
            queryFn: async variables => {
                try {
                    const { data } = await client.graphql({
                        query: createFleet,
                        variables: {
                            input: variables,
                        },
                    });
                    return { data: SystemFleetSchema().parse(data.createFleet) };
                } catch (error) {
                    throw errorHandler(
                        `Unexpected error occurred while creating the fleet with id: ${variables.name}`,
                        error
                    );
                }
            },
        }),
    }),
});

export const {
    useListSystemFleetsQuery,
    useGetSystemFleetsAttributesQuery,
    useUpdateSystemFleetMutation,
    useCreateSystemFleetMutation,
} = systemFleetsApi;
